import * as XLSX from "xlsx";
import { normalizeHeader, sha256Hex } from "./parse";
import type { RowIssue, ValidationStatus } from "./types";

/** Zero/negative/missing cost is unknown for valuation, not a confirmed cost. */
export function isUsableCost(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value) && value > 0;
}

export type CanonicalStockRow = {
  store_name: string | null;
  sku: string | null;
  barcode: string | null;
  product_name: string | null;
  color_code: string | null;
  color_name: string | null;
  size: string | null;
  inventory_qty: number | null;
  reserved_qty: number | null;
  remaining_shipment_qty: number | null;
  packed_pending_qty: number | null;
  available_qty: number | null;
  future_inventory_qty: number | null;
  ordered_qty: number | null;
  minimum_qty: number | null;
  maximum_qty: number | null;
  cost_price: number | null;
  cost_total: number | null;
  /** Nebim's dealer purchase price; VAT treatment is not assumed. */
  dealer_purchase_price: number | null;
  ordered_remaining_qty?: number | null;
  unapproved_shipment_qty?: number | null;
  incoming_order_remaining_qty?: number | null;
  warehouse_location?: string | null;
  supplier_company?: string | null;
  product_group?: string | null;
  season?: string | null;
  collection?: string | null;
  stock_dimension_2?: string | null;
  stock_dimension_3?: string | null;
  manufacturer_group?: string | null;
  main_category?: string | null;
  upper_category?: string | null;
  gender?: string | null;
  category?: string | null;
  subcategory?: string | null;
  product_hierarchy_path?: string | null;
  ps_value?: string | null;
};

export type ParsedStockRow = {
  row_index: number;
  raw: Record<string, unknown>;
  parsed: CanonicalStockRow;
  status: ValidationStatus;
  issues: RowIssue[];
};

export type StockParseResult = {
  file_name: string;
  file_hash: string;
  report_type: "nebim_stock";
  header_row_index: number | null;
  row_count: number;
  valid_count: number;
  warn_count: number;
  error_count: number;
  missing_required: string[];
  warehouses: string[];
  rows: ParsedStockRow[];
};

const ALIASES: Record<string, keyof CanonicalStockRow> = {
  // Different Nebim layouts label the same warehouse field differently.
  depo: "store_name",
  depo_adi: "store_name",
  magaza: "store_name",
  magaza_adi: "store_name",
  warehouse: "store_name",
  warehouse_name: "store_name",
  store: "store_name",
  store_name: "store_name",
  urun_kodu: "sku",
  barkod: "barcode",
  urun_adi: "product_name",
  renk_kodu: "color_code",
  renk_aciklamasi: "color_name",
  beden: "size",
  envanter: "inventory_qty",
  rezerv_edilmis_bekleyen: "reserved_qty",
  rezerv_edilen: "reserved_qty",
  rezerve_edilen: "reserved_qty",
  kalan_sevk_emri: "remaining_shipment_qty",
  kalan_sevk_emri_miktari: "remaining_shipment_qty",
  paketlenmis_bekleyen: "packed_pending_qty",
  paketlenmis_bekleyen_miktar: "packed_pending_qty",
  kullanilabilir_envanter: "available_qty",
  gelecek_urunler_dahil_envanter: "future_inventory_qty",
  verilen_siparis_miktari: "ordered_qty",
  minimum_miktar: "minimum_qty",
  minumum_miktar: "minimum_qty",
  min_miktar: "minimum_qty",
  maksimum_miktar: "maximum_qty",
  max_miktar: "maximum_qty",
  bayi_alis_fiyati: "dealer_purchase_price",
  bayi_alis_fiyati_tl: "dealer_purchase_price",
  maliyet_fiyati: "cost_price",
  birim_maliyet: "cost_price",
  maliyet_tutari: "cost_total",
  toplam_maliyet: "cost_total",
  verilen_siparis_kalani: "ordered_remaining_qty",
  onaylanmamis_sevkler: "unapproved_shipment_qty",
  alinan_siparis_kalani: "incoming_order_remaining_qty",
  depoda_bulundugu_yer: "warehouse_location",
  tedarikci_firma: "supplier_company",
  tedarikci_sirketi: "supplier_company",
  supplier_company: "supplier_company",
  warehouse_location: "warehouse_location",
  urun_grubu: "product_group",
  product_group: "product_group",
  sezon: "season",
  season: "season",
  koleksiyon: "collection",
  collection: "collection",
  stok_boyutu_2: "stock_dimension_2",
  stock_dimension_2: "stock_dimension_2",
  stok_boyutu_3: "stock_dimension_3",
  stock_dimension_3: "stock_dimension_3",
  uretici_grubu: "manufacturer_group",
  manufacturer_group: "manufacturer_group",
  ana_kategori: "main_category",
  main_category: "main_category",
  ust_kategori: "upper_category",
  upper_category: "upper_category",
  cinsiyet: "gender",
  gender: "gender",
  kategori: "category",
  category: "category",
  alt_kategori: "subcategory",
  subcategory: "subcategory",
  urun_hiyerarsisindeki_yeri: "product_hierarchy_path",
  product_hierarchy_path: "product_hierarchy_path",
  ps: "ps_value",
  ps_value: "ps_value",
};

export function resolveStockHeader(header: string): keyof CanonicalStockRow | null {
  return ALIASES[normalizeHeader(header)] ?? null;
}

export function parseNebimStockNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  let text = String(value).trim().replace(/[%\s₺$€£]/g, "");
  if (!text || !/[0-9]/.test(text)) return null;

  // Nebim/TR Excel exports can contain either 1.234,56 or 1,234.56.
  // When both separators exist, the rightmost separator is the decimal mark.
  let sign = 1;
  if (/^\(.*\)$/.test(text)) {
    sign = -1;
    text = text.slice(1, -1);
  }

  const hasComma = text.includes(",");
  const dotCount = (text.match(/\./g) ?? []).length;
  let normalized: string;

  if (hasComma && dotCount > 0) {
    normalized = text.lastIndexOf(",") > text.lastIndexOf(".")
      ? text.replace(/\./g, "").replace(",", ".")
      : text.replace(/,/g, "");
  } else if (hasComma) {
    const commaCount = (text.match(/,/g) ?? []).length;
    if (commaCount > 1) {
      normalized = text.replace(/,/g, "");
    } else {
      // Mirror the dot rule: a single separator followed by three digits is
      // treated as a thousands separator (e.g. "1,234" -> 1234).
      const commaIndex = text.lastIndexOf(",");
      const digitsAfter = text.length - commaIndex - 1;
      normalized = digitsAfter === 3 ? text.replace(",", "") : text.replace(",", ".");
    }
  } else if (dotCount > 1) {
    normalized = text.replace(/\./g, "");
  } else if (dotCount === 1) {
    // Turkish thousands separator: 2.199 means 2199, not 2.199.
    const dotIndex = text.lastIndexOf(".");
    const digitsAfter = text.length - dotIndex - 1;
    normalized = digitsAfter === 3 ? text.replace(".", "") : text;
  } else {
    normalized = text;
  }

  const result = Number(normalized);
  return Number.isFinite(result) ? sign * result : null;
}

function parseText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text && text.toLowerCase() !== "null" ? text : null;
}

function isEmptyRow(row: unknown[]): boolean {
  return row.every((value) => value == null || String(value).trim() === "");
}

/** True when a non-empty worksheet row contains no values in any mapped stock field.
 * This filters report footers (for example, company/page/date labels) without
 * hiding malformed product rows that contain a SKU, barcode, or quantity. */
export function isEmptyMappedStockRow(parsed: CanonicalStockRow): boolean {
  // Warehouse/company labels can appear in report footers; they are not product data.
  // Keep any row that has a product identifier, product description, or stock/financial value.
  return !parsed.sku && !parsed.barcode && !parsed.product_name &&
    parsed.inventory_qty == null && parsed.reserved_qty == null &&
    parsed.remaining_shipment_qty == null && parsed.packed_pending_qty == null &&
    parsed.available_qty == null && parsed.future_inventory_qty == null &&
    parsed.ordered_qty == null && parsed.minimum_qty == null &&
    parsed.maximum_qty == null && parsed.cost_price == null && parsed.cost_total == null &&
    parsed.dealer_purchase_price == null &&
    parsed.ordered_remaining_qty == null && parsed.unapproved_shipment_qty == null &&
    parsed.incoming_order_remaining_qty == null;
}

export async function parseNebimStockExcel(file: File): Promise<StockParseResult> {
  const buffer = await file.arrayBuffer();
  const file_hash = await sha256Hex(buffer);
  const empty: StockParseResult = {
    file_name: file.name,
    file_hash,
    report_type: "nebim_stock",
    header_row_index: null,
    row_count: 0,
    valid_count: 0,
    warn_count: 0,
    error_count: 0,
    missing_required: ["sku"],
    warehouses: [],
    rows: [],
  };

  const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error("Excel dosyasında okunabilir sayfa bulunamadı.");
  const sheet = workbook.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: false,
    defval: "",
    blankrows: true,
  });

  // Preserve values from merged Excel cells exactly as displayed by Excel.
  // Nebim exports may merge product identifiers or warehouse labels vertically.
  const merges = (sheet as XLSX.WorkSheet)["!merges"];
  if (Array.isArray(merges)) {
    for (const merge of merges) {
      const topRow = matrix[merge.s.r];
      const topLeft = topRow?.[merge.s.c];
      if (topLeft == null || String(topLeft).trim() === "") continue;
      for (let rowIndex = merge.s.r; rowIndex <= merge.e.r; rowIndex++) {
        if (!matrix[rowIndex]) matrix[rowIndex] = [];
        for (let columnIndex = merge.s.c; columnIndex <= merge.e.c; columnIndex++) {
          if (rowIndex === merge.s.r && columnIndex === merge.s.c) continue;
          const current = matrix[rowIndex][columnIndex];
          if (current == null || String(current).trim() === "") {
            matrix[rowIndex][columnIndex] = topLeft;
          }
        }
      }
    }
  }

  const headerLimit = Math.min(matrix.length, 40);
  let headerIndex = -1;
  let bestScore = 0;
  let bestMap: Record<number, keyof CanonicalStockRow> = {};
  for (let r = 0; r < headerLimit; r++) {
    const mapping: Record<number, keyof CanonicalStockRow> = {};
    matrix[r].forEach((cell, col) => {
      const key = normalizeHeader(String(cell ?? ""));
      const canonical = resolveStockHeader(String(cell ?? ""));
      if (canonical) mapping[col] = canonical;
    });
    const score = Object.keys(mapping).length;
    if (score > bestScore) {
      bestScore = score;
      bestMap = mapping;
      headerIndex = r;
    }
  }

  if (headerIndex < 0 || bestScore < 6) {
    return {
      ...empty,
      missing_required: ["Depo Adı", "Ürün Kodu veya Barkod", "Envanter", "Kullanılabilir Envanter"],
    };
  }

  const mappedFields = new Set(Object.values(bestMap));
  const missing_required: string[] = [];
  if (!mappedFields.has("store_name")) missing_required.push("Depo Adı");
  if (!mappedFields.has("sku") && !mappedFields.has("barcode")) missing_required.push("Ürün Kodu veya Barkod");
  if (!mappedFields.has("inventory_qty") && !mappedFields.has("available_qty")) {
    missing_required.push("Envanter veya Kullanılabilir Envanter");
  }
  const rows: ParsedStockRow[] = [];
  const warehouses = new Set<string>();

  for (let index = headerIndex + 1; index < matrix.length; index++) {
    const cells = matrix[index];
    if (isEmptyRow(cells)) continue;
    const raw: Record<string, unknown> = {};
    const parsed: CanonicalStockRow = {
      store_name: null, sku: null, barcode: null, product_name: null,
      color_code: null, color_name: null, size: null, inventory_qty: null,
      reserved_qty: null, remaining_shipment_qty: null, packed_pending_qty: null,
      available_qty: null, future_inventory_qty: null, ordered_qty: null,
      minimum_qty: null, maximum_qty: null, cost_price: null, cost_total: null, dealer_purchase_price: null,
    };
    // Preserve every original, non-empty source column in raw_payload so the
    // hierarchy fields that are not used by calculations remain auditable.
    for (let sourceCol = 0; sourceCol < matrix[headerIndex].length; sourceCol++) {
      const sourceHeader = String(matrix[headerIndex][sourceCol] ?? "").trim();
      const sourceValue = cells[sourceCol];
      if (sourceHeader && sourceValue != null && String(sourceValue).trim() !== "") {
        raw[sourceHeader] = sourceValue;
      }
    }

    for (const [colText, canonical] of Object.entries(bestMap)) {
      const col = Number(colText);
      const header = String(matrix[headerIndex][col] ?? "").trim();
      const value = cells[col];
      if (["store_name", "sku", "barcode", "product_name", "color_code", "color_name", "size",
        "warehouse_location", "supplier_company", "product_group", "season", "collection",
        "stock_dimension_2", "stock_dimension_3", "manufacturer_group", "main_category",
        "upper_category", "gender", "category", "subcategory", "product_hierarchy_path", "ps_value"].includes(canonical)) {
        parsed[canonical] = parseText(value) as never;
      } else {
        parsed[canonical] = parseNebimStockNumber(value) as never;
      }
    }

    // Skip report totals and pagination/footer lines that are not product records.
    // The IFM workbook includes a totals row with inventory/cost values but no store or
    // product identity, plus a legal-company/page footer that can be misread through
    // merged cells as SKU "1 / 1". Neither row represents stock and must not be staged.
    const hasProductIdentity = Boolean(parsed.sku || parsed.barcode || parsed.product_name);
    const hasStore = Boolean(parsed.store_name);
    const hasStockMeasure = parsed.inventory_qty != null || parsed.available_qty != null ||
      parsed.reserved_qty != null || parsed.remaining_shipment_qty != null ||
      parsed.packed_pending_qty != null || parsed.future_inventory_qty != null ||
      parsed.ordered_qty != null || parsed.ordered_remaining_qty != null ||
      parsed.unapproved_shipment_qty != null || parsed.incoming_order_remaining_qty != null;
    const isPageCounter = Boolean(parsed.sku && /^\d+\s*\/\s*\d+$/.test(parsed.sku));
    if (isEmptyMappedStockRow(parsed) ||
        (!hasStore && !hasProductIdentity) ||
        (isPageCounter && !hasStockMeasure)) continue;

    // Do not silently discard a non-empty row with missing product identity.
    // Keep it in the preview as a validation error so the operator can inspect it.
    if (parsed.store_name) warehouses.add(parsed.store_name);
    const issues: RowIssue[] = [];
    if (!parsed.sku && !parsed.barcode) {
      issues.push({ code: "stock_identity_missing", message: "Ürün kodu ve barkod boş", severity: "error" });
    }
    if (parsed.inventory_qty == null && parsed.available_qty == null) {
      issues.push({ code: "stock_quantity_missing", message: "Envanter ve kullanılabilir envanter alanları boş", severity: "error" });
    }
    if (parsed.inventory_qty != null && parsed.inventory_qty < 0) {
      issues.push({ code: "negative_inventory_quantity", message: "Kaynak dosyada negatif envanter miktarı var; kaynak değer korunacak ve stok görüntüsü uyarılı kalacak.", severity: "warning" });
    }
    if (parsed.inventory_qty != null && parsed.available_qty != null && parsed.available_qty > parsed.inventory_qty + 0.001) {
      issues.push({ code: "available_exceeds_inventory", message: "Kullanılabilir envanter toplam envanteri aşıyor", severity: "warning" });
    }
    const status = issues.some((issue) => issue.severity === "error")
      ? "error"
      : issues.some((issue) => issue.severity === "warning") ? "warn" : "ok";
    rows.push({ row_index: index + 1, raw, parsed, status, issues });
  }

  const warehouseList = Array.from(warehouses);

  return {
    file_name: file.name,
    file_hash,
    report_type: "nebim_stock",
    header_row_index: headerIndex + 1,
    row_count: rows.length,
    valid_count: rows.filter((row) => row.status === "ok").length,
    warn_count: rows.filter((row) => row.status === "warn").length,
    error_count: rows.filter((row) => row.status === "error").length,
    missing_required,
    warehouses: warehouseList,
    rows,
  };
}
